import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getProfile } from '../../redux/modules/profile/selectors';
import { Box } from '@mui/material';
import { fontWeight, IconButton, Text } from '../../packages';
import { Drawer, drawerType } from '../../packages/ui/organisms/drawer';
import {
  fetchAMAState,
  postAMA,
  patientCaseConceptualizer,
  patientEngagementInsights,
  milestoneCelebrator,
} from '../../services/report-service/reportService.service';
import { useStyles } from './AskMeAnything.styles';
import AskMeAnythingSession from './components/AskMeAnythingSession';

const AskMeAnything = ({ isOpen = false, onClose = null, memberId, useDrawer = false }) => {
  const classes = useStyles();
  const { profile } = useSelector(getProfile);
  const TIME_LIMIT = 30000;
  const [question, setQuestion] = useState(null);
  const [response, setResponse] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [selectedCopilotOption, setSelectedCopilotOption] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const copilotStarterPrompts = {
    'patient-case-conceptualizer': 'Help me conceptualize this patient case',
    'patient-engagement-insights': 'Provide general insights on patient engagement',
    'milestone-celebrator': 'Help me celebrate patient milestones',
  };

  useEffect(() => {
    setConversationHistory([]);
    setSessionId(null);
    setResponse(null);
    if (!selectedCopilotOption) return;

    const copilotApiMap = {
      'patient-case-conceptualizer': patientCaseConceptualizer,
      'patient-engagement-insights': patientEngagementInsights,
      'milestone-celebrator': milestoneCelebrator,
    };

    const runCopilotAutoStart = async option => {
      if (!memberId || !profile?.providerId) return;
      const starterPrompt = copilotStarterPrompts[option];
      const copilotApi = copilotApiMap[option];
      if (!starterPrompt || !copilotApi) return;

      const extraPayload = option === 'milestone-celebrator' ? { providerId: profile?.providerId } : {};

      setIsLoadingResponse(true);
      setIsError(false);

      try {
        // 1) Handshake call (backend may return a welcome message + sessionId).
        // We keep this message hidden and only use it to establish a session.
        const handshakeResponse = await copilotApi({
          patientId: memberId,
          message: 'Hello',
          ...extraPayload,
        });

        const newSessionId = handshakeResponse?.data?.message?.sessionId;
        if (newSessionId) {
          setSessionId(newSessionId);
        }

        // 2) Send the actual starter prompt (this should generate the real response).
        const sentMessage = { type: 'sent', content: starterPrompt };
        setConversationHistory([sentMessage]);
        setQuestion('');

        const promptResponse = await copilotApi({
          patientId: memberId,
          message: starterPrompt,
          ...(newSessionId ? { sessionId: newSessionId } : {}),
          ...extraPayload,
        });

        if (promptResponse.data?.success) {
          const responseData = promptResponse.data;

          if (responseData.message?.sessionId) {
            setSessionId(responseData.message.sessionId);
          }

          if (responseData.message?.response) {
            // Immediate response: set it and stop loading
            setResponse(responseData.message.response);
            const receivedMessage = { type: 'received', content: responseData.message.response };
            setConversationHistory([sentMessage, receivedMessage]);
            setIsLoadingResponse(false);
          } else if (responseData.identifier) {
            // Async response: start polling (getAskedQuestionState will stop loading when done)
            await getAskedQuestionState(responseData.identifier);
          } else {
            // No response and no identifier: stop loading
            setIsLoadingResponse(false);
          }
        } else {
          // Request didn't succeed: stop loading
          setIsLoadingResponse(false);
        }
      } catch (error) {
        console.error('Error in copilot auto-start:', error);
        setIsError(true);
        setIsLoadingResponse(false);
      }
    };

    void runCopilotAutoStart(selectedCopilotOption);
  }, [selectedCopilotOption]);

  useEffect(() => {
    return () => {
      setSessionId(null);
    };
  }, []);

  const buildCopilotPayload = () => {
    const basePayload = {
      patientId: memberId,
      message: question,
    };

    if (sessionId) {
      basePayload.sessionId = sessionId;
    }

    switch (selectedCopilotOption) {
      case 'patient-case-conceptualizer':
        return basePayload;

      case 'patient-engagement-insights':
        return basePayload;

      case 'milestone-celebrator':
        return {
          ...basePayload,
          providerId: profile?.providerId,
        };

      default:
        return basePayload;
    }
  };

  const callCopilotAPI = async payload => {
    switch (selectedCopilotOption) {
      case 'patient-case-conceptualizer':
        return await patientCaseConceptualizer(payload);

      case 'patient-engagement-insights':
        return await patientEngagementInsights(payload);

      case 'milestone-celebrator':
        return await milestoneCelebrator(payload);

      default:
        throw new Error(`Unknown co-pilot option: ${selectedCopilotOption}`);
    }
  };

  const getAskedQuestionState = async (identifier, startTime = Date.now()) => {
    try {
      const pathParams = { providerId: profile?.providerId, memberId, identifier };
      const stateResponse = await fetchAMAState(pathParams);
      if (stateResponse.data.state?.status === 'COMPLETED') {
        setIsLoadingResponse(false);
        const responseText = stateResponse.data.state?.response;
        setResponse(responseText);

        if (responseText) {
          const receivedMessage = {
            type: 'received',
            content: responseText,
          };

          if (!selectedCopilotOption) {
            setConversationHistory(prev => {
              const currentSentMessage = prev.find(msg => msg.type === 'sent');
              return currentSentMessage ? [currentSentMessage, receivedMessage] : [receivedMessage];
            });
          } else {
            setConversationHistory(prev => [...prev, receivedMessage]);
          }
        }
      } else {
        const elapsedTime = Date.now() - startTime;

        if (elapsedTime >= TIME_LIMIT) {
          setIsLoadingResponse(false);
          setResponse('This question is a little too complicated for me right now.');
        } else {
          setTimeout(() => {
            void getAskedQuestionState(identifier, startTime);
          }, 4000);
        }
      }
    } catch (e) {
      console.warn(e);
      setIsLoadingResponse(false);
      setIsError(true);
    }
  };

  const handleSubmitQuestion = async () => {
    try {
      setIsLoadingResponse(true);
      const identifier = Date.now();
      const sentMessage = {
        type: 'sent',
        content: question,
      };

      if (!selectedCopilotOption) {
        setConversationHistory([sentMessage]);
      } else {
        setConversationHistory(prev => [...prev, sentMessage]);
      }

      setQuestion('');

      if (selectedCopilotOption) {
        const copilotPayload = buildCopilotPayload();

        const copilotResponse = await callCopilotAPI(copilotPayload);
        if (copilotResponse.data?.success) {
          const responseData = copilotResponse.data;

          if (responseData.message?.sessionId) {
            setSessionId(responseData.message.sessionId);
          }

          if (responseData.message?.response) {
            setIsLoadingResponse(false);
            setResponse(responseData.message.response);

            const receivedMessage = {
              type: 'received',
              content: responseData.message.response,
            };
            setConversationHistory(prev => [...prev, receivedMessage]);
          } else if (responseData.identifier) {
            await getAskedQuestionState(responseData.identifier);
          }
        }
      } else {
        const bodyRequest = {
          memberId,
          providerId: profile?.providerId,
          question,
          identifier,
        };

        const invokeResponse = await postAMA(bodyRequest);
        if (invokeResponse.data?.success) {
          await getAskedQuestionState(identifier);
        }
      }
    } catch (e) {
      console.warn(e);
    }
  };

  const handleCopilotAction = action => {
    switch (action) {
      case 'patient-case-conceptualizer':
        break;
      case 'patient-engagement-insights':
        break;
      case 'milestone-celebrator':
        break;
      default:
        break;
    }
  };

  const renderSessionAMA = () => (
    <AskMeAnythingSession
      memberId={memberId}
      question={question}
      response={response}
      setQuestion={setQuestion}
      onSubmit={handleSubmitQuestion}
      onCopilotAction={handleCopilotAction}
      selectedCopilotOption={selectedCopilotOption}
      setSelectedCopilotOption={setSelectedCopilotOption}
      conversationHistory={conversationHistory}
      isLoading={isLoadingResponse}
      isError={isError}
    />
  );

  return (
    <>
      {useDrawer ? (
        <Drawer open={isOpen} onClose={onClose} variant={drawerType.NORMAl} className={classes.drawer}>
          <Box className={classes.wrapper}>
            <Box className={classes.header}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Text weight={fontWeight.BOLD} className={classes.headTitle}>
                  Confidant Co-Pilot
                </Text>
                <IconButton icon="close" className={classes.closeBtn} onClick={onClose} />
              </Box>
            </Box>
            <Box className={classes.content}>{renderSessionAMA()}</Box>
          </Box>
        </Drawer>
      ) : (
        renderSessionAMA()
      )}
    </>
  );
};

export { AskMeAnything };
